import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

import surveyData from "@/assets/data/survey_data.json";
import filters from "@/store/filters.js";

import {
  countOfSingleChoice,
  countOfMultipleChoice,
  resolutionSankeyDiagram,
  scatterHelpfulness,
  subLegalProblems,
  reasonNoAction,
  impactIndex
} from "@/store/charts.js";

export const store = new Vuex.Store({
  state: {
    surveyData,
    filtersCategories: filters.categories,
    filtersOptions: filters.options,
    filtersSelected: filters.selected,
    currentSection: 0,
    filtersShown: false,
    drawerOpen: null
  },
  mutations: {
    updateLegalProblemType: (state, legalProblemType) => {
      state.filtersSelected.legalProblemType = legalProblemType;
    },
    updateLegalProblem: (state, legalProblem) => {
      state.filtersSelected.legalProblem = legalProblem;
    },
    updateFilter: (state, { filter, value }) => {
      state.filtersSelected[filter] = value;
    },
    updateCurrentSection: (state, currentSection) => {
      state.currentSection = currentSection;
    },
    showFilters: state => {
      state.filtersShown = true;
    },
    toggleDrawer: (state, value) => {
      state.drawerOpen = value;
    },
    resetFilters: state => {
      state.filtersSelected.focusOnOneLegalProblem = false;
      state.filtersSelected.legalProblemType =
        state.filtersOptions.legalProblemType[0];
      state.filtersSelected.legalProblem = state.filtersOptions.legalProblem[0];
      state.filtersCategories.demographics.forEach(
        key => (state.filtersSelected[key] = state.filtersOptions[key].slice())
      );
      state.filtersCategories.resolutions.forEach(
        key => (state.filtersSelected[key] = state.filtersOptions[key].slice())
      );
    }
  },
  actions: {
    updateLegalProblemType: ({ commit, state }, legalProblemType) => {
      commit("updateLegalProblemType", legalProblemType);
      if (!state.filterSelected.legalProblem) {
        commit("updateLegalProblem", state.filterOptions.legalProblem[0]);
      }
    },
    updateLegalProblem: ({ commit }, legalProblem) => {
      commit("updateLegalProblem", legalProblem);
    },
    updateFilter: ({ commit }, filterObj) => {
      commit("updateFilter", filterObj);
    },
    updateCurrentSection: ({ commit }, currentSection) => {
      commit("updateCurrentSection", currentSection);
    },
    showFilters: ({ commit }) => {
      commit("showFilters");
      commit("toggleDrawer", true);
    },
    toggleDrawer: ({ commit }, value) => {
      commit("toggleDrawer", value);
    },
    resetFilters: ({ commit }) => {
      commit("resetFilters");
    }
  },
  getters: {
    demographicsFilters: state => state.filtersCategories.demographics,
    resolutionsFilters: state => state.filtersCategories.resolutions,
    demAndResFilters: (state, getters) => [
      ...getters.demographicsFilters,
      ...getters.resolutionsFilters
    ],
    appliedDemographicsFilters: (state, getters) =>
      getters.demographicsFilters.filter(filter => {
        return (
          JSON.stringify(state.filtersSelected[filter]) !==
          JSON.stringify(state.filtersOptions[filter])
        );
      }),
    appliedResolutionsFilters: (state, getters) =>
      getters.resolutionsFilters.filter(filter => {
        return (
          JSON.stringify(state.filtersSelected[filter]) !==
          JSON.stringify(state.filtersOptions[filter])
        );
      }),
    filtersApplied: (state, getters) => {
      if (
        getters.appliedDemographicsFilters.length === 0 &&
        getters.appliedResolutionsFilters.length === 0 &&
        state.filtersSelected.focusOnOneLegalProblem === false &&
        state.filtersSelected.legalProblemType ===
          state.filtersOptions.legalProblemType[0] &&
        state.filtersSelected.legalProblem ===
          state.filtersOptions.legalProblem[0]
      ) {
        return false;
      } else {
        return true;
      }
    },
    experiencedData: state =>
      state.surveyData.filter(d => d.legalProblemsExperienced.length > 0),
    filteredData: (state, getters) => {
      // Experienced legal problems?
      let filteredData = getters.experiencedData;

      // Demographic and resolution filters
      if (
        getters.demAndResFilters
          .map(filter => state.filtersSelected[filter])
          .some(selected => selected.length === 0)
      ) {
        return [];
      }

      filteredData = filteredData.filter(d => {
        for (const filter of getters.appliedDemographicsFilters) {
          if (!state.filtersSelected[filter].includes(d[filter])) {
            return false;
          }
        }

        if (state.filtersSelected.experiencedLegalProblems) {
          for (const filter of getters.appliedResolutionsFilters) {
            if (filter === "resolutionStrategy") {
              if (
                d.attemptedResolution === 2 &&
                state.filtersSelected.attemptedResolution.includes(2)
              ) {
                // Not attempted selected, and the respondent not attempted
              } else if (
                d[filter].some(s => !state.filtersSelected[filter].includes(s))
              ) {
                // Selected strategies don't include all of the respondent' strategies
                return false;
              }
            } else {
              if (!state.filtersSelected[filter].includes(d[filter]))
                return false;
            }
          }
        }

        return true;
      });

      // Focus on one legal problem?
      if (
        state.filtersSelected.experiencedLegalProblems &&
        state.filtersSelected.focusOnOneLegalProblem
      ) {
        filteredData = filteredData.filter(d => {
          // Experience problems
          if (state.filtersSelected.legalProblemType === 1) {
            if (
              !d.legalProblemsExperienced.includes(
                state.filtersSelected.legalProblem
              )
            )
              return false;
          }
          // Most severe problem
          if (state.filtersSelected.legalProblemType === 2) {
            if (d.severeLegalProblem !== state.filtersSelected.legalProblem)
              return false;
          }
          return true;
        });
      }
      return filteredData;
    },
    nonExperiencedData: state =>
      state.surveyData.filter(d => d.legalProblemsExperienced.length === 0),
    filteredDataNoLP: (state, getters) => {
      // Experienced legal problems?
      let filteredData = getters.nonExperiencedData;
      // Demographic filters
      if (
        getters.demographicsFilters
          .map(filter => state.filtersSelected[filter])
          .some(selected => selected.length === 0)
      ) {
        return [];
      }

      filteredData = filteredData.filter(d => {
        for (const filter of getters.appliedDemographicsFilters) {
          if (!state.filtersSelected[filter].includes(d[filter])) {
            return false;
          }
        }
        return true;
      });
      return filteredData;
    },
    filterSelected: state => filter => state.filtersSelected[filter],
    filterOptions: state => filter => state.filtersOptions[filter],
    totalCount: state => state.surveyData.length,
    experiencedCount: (state, getters) => getters.experiencedData.length,
    // TODO CHECK
    // Zhenmao how often is this filtered count being executed?
    filteredCount: (state, getters) => getters.filteredData.length,
    // It's being called in MapDemographics and i'm wondering if it has to check the length every time it calculates the value for the respondent.
    filteredDemographicData: (state, getters) => filter =>
      countOfSingleChoice(
        getters.filteredData,
        getters.filterOptions(filter),
        filter
      ),
    filteredBarChartExperienced: (state, getters) => () =>
      countOfMultipleChoice(
        getters.filteredData,
        getters.filterOptions("legalProblem"),
        "legalProblemsExperienced"
      ),
    filteredBarChartSevere: (state, getters) => () =>
      countOfSingleChoice(
        getters.filteredData,
        getters.filterOptions("legalProblem"),
        "severeLegalProblem"
      ),
    filteredSankeyResolutonPathData: (state, getters) => () =>
      resolutionSankeyDiagram(getters.filteredData),
    filteredScatterHelpfulnessData: (state, getters) => () =>
      scatterHelpfulness(getters.filteredData, getters),
    filteredSubLegalProblemsData: (state, getters) => () =>
      subLegalProblems(getters.filteredData, getters.filteredDataNoLP),
    filteredReasonNoActionData: (state, getters) => () =>
      reasonNoAction(getters.filteredData),
    filteredImpactIndexData: (state, getters) => () =>
      impactIndex(getters.filteredData)
  }
});
